import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: 85, suffix: "M+", prefix: "AED ", label: "Revenue Generated" },
  { value: 180, suffix: "K+", label: "Qualified Leads" },
  { value: 35, suffix: "M+", prefix: "AED ", label: "Ad Spend Managed" },
  { value: 14, suffix: "+", label: "Countries Served" },
  { value: 120, suffix: "+", label: "Projects Delivered" },
];

function Counter({ value, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-clash text-4xl md:text-5xl font-semibold text-white">
      {prefix}
      {n}
      {suffix}
    </span>
  );
}

export default function TrustStats() {
  return (
    <section
      id="trust"
      data-testid="trust-stats"
      className="relative py-14 md:py-28 border-b border-white/5 overflow-hidden"
    >
      <div className="aurora-soft" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div>
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
              / Proof of impact
            </div>
            <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">
              Numbers that move boardrooms.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm md:text-base">
            We measure success in revenue, not vanity metrics. Every engagement
            is benchmarked against P&L outcomes.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-px rounded-3xl overflow-hidden glass-strong">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-[#0a0a0c] px-6 py-8 hover:bg-[#0f0f12] transition-colors"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              <div className="mt-3 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
